import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';


// Standard meal options
const MEAL_OPTIONS = [
  'Breakfast',
  'Snack AM',
  'Lunch',
  'Side Dish Lunch',
  'Snack PM',
  'Dinner',
  'Side Dish Dinner',
  'Iftar Fruits',
  'Iftar Drinks',
  'Iftar Soup',
  'Iftar Appetizers',
  'Iftar Main Dish',
  'Iftar Salad',
  'Iftar Dessert',
  'Souhour',
] as const;



const MenuManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [menuName, setMenuName] = React.useState('');
  const [selectedDietId, setSelectedDietId] = React.useState('');
  const [copyFromMenuId, setCopyFromMenuId] = React.useState('');
  const [rows, setRows] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  // Track which (week, meal) groups were loaded so we can delete groups that are removed by the user
  const [loadedGroups, setLoadedGroups] = React.useState<Array<{ week: string; meal: string }>>([]);

  const { data: menuData, isLoading } = useQuery({
    queryKey: ['menu', id],
    queryFn: () => dietService.getMenu(id!),
    enabled: !!id && id !== 'new',
  });

  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-menu'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000, status: 'ACTIVE' }),
  });

  const { data: menusData } = useQuery({
    queryKey: ['menus', 'for-copy'],
    queryFn: () => dietService.getMenus({ limit: 1000 }),
  });

  // When copying from a menu (creating a new one), fetch its details to know periodWeeks
  const { data: copyFromMenuData } = useQuery({
    queryKey: ['menu', 'copyFrom', copyFromMenuId],
    queryFn: () => dietService.getMenu(copyFromMenuId),
    enabled: !!copyFromMenuId,
  });

  const { data: platesData } = useQuery({
    queryKey: ['recipe-plates', 'for-menu'],
    queryFn: () => dietService.getRecipePlates({ limit: 1000, status: 'ACTIVE' }),
  });

  const plateOptions = React.useMemo(() => {
    const plates = platesData?.recipePlates || [];
    // Deduplicate by profile name - show each profile only once
    const seen = new Set<string>();
    const options: Array<{ id: string; label: string }> = [];

    for (const p of plates) {
      const profileName = (p as any).profileName || p.name;
      if (!seen.has(profileName)) {
        seen.add(profileName);
        options.push({
          id: p.id,
          label: profileName,
        });
      }
    }
    return options;
  }, [platesData]);

  React.useEffect(() => {
    if (menuData?.menu) {
      setMenuName(menuData.menu.name);
      if (menuData.menu.dietId) {
        setSelectedDietId(menuData.menu.dietId);
      }
      // Auto-load the first week/meal data when menu loads
      if (id && id !== 'new' && rows.length === 0) {
        loadFirstWeekMeal(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuData, id]);

  const loadFirstWeekMeal = async (menuId: string) => {
    try {
      console.log('Auto-loading ALL week/meal groups for menu:', menuId);
      // Get available week/meal combinations
      const availRes = await dietService.getAvailableMenuDayPlates(menuId);
      console.log('Available week/meal combinations:', availRes);
      const available = (availRes as any)?.items || [];

      if (available.length === 0) {
        console.log('No saved schedules found');
        return;
      }

      // Build rows for ALL available groups (so nothing “disappears” when reopening)
      const allRows: any[] = [];
      const toKey = (d: number) => (d===1?'mon':d===2?'tue':d===3?'wed':d===4?'thu':d===5?'fri':d===6?'sat':'sun');

      const combos = (available as any[]).map((a: any) => ({
        week: a.week === null ? 'all' : String(a.week),
        meal: String(a.mealType)
      }));

      for (const combo of combos) {
        const week = combo.week; const meal = combo.meal;
        console.log('Loading group → week:', week, 'meal:', meal);
        const res = await dietService.getMenuDayPlates(menuId, { week, meal });
        const items = (res as any)?.items || [];
        if (items.length > 0) {
          const bySort: Record<number, any> = {};
          for (const it of items) {
            const s = Number((it as any).sortOrder || 0);
            if (!bySort[s]) bySort[s] = { week, meal, mon:'',tue:'',wed:'',thu:'',fri:'',sat:'',sun:'' };
            const k = toKey(Number((it as any).day));
            bySort[s][k] = (it as any).recipePlateId;
          }
          const groupRows = Object.keys(bySort).map(n => Number(n)).sort((a,b)=>a-b).map(s => bySort[s]);
          allRows.push(...groupRows);
        }
      }

      // Remember which groups were loaded to support deletion on Save
      setLoadedGroups(combos);

      console.log('Setting rows with all groups:', allRows);
      if (allRows.length > 0) {
        setRows(allRows);
      }
    } catch (e: any) {
      console.error('Auto-load failed:', e);
    }
  };

  // Determine allowed weeks based on the plan (diet.periodWeeks) of the selected/current menu
  const periodWeeks: number | null = React.useMemo(() => {
    // First, try to get from selected diet
    if (selectedDietId && dietsData?.diets) {
      const selectedDiet = dietsData.diets.find(d => d.id === selectedDietId);
      if (selectedDiet?.periodWeeks) {
        return selectedDiet.periodWeeks;
      }
    }
    // Then try current menu's diet
    const currentWeeks = (menuData?.menu?.diet as any)?.periodWeeks as number | undefined;
    // Then try copy from menu's diet
    const copyWeeks = (copyFromMenuData?.menu?.diet as any)?.periodWeeks as number | undefined;
    return (currentWeeks ?? copyWeeks ?? null) as number | null;
  }, [selectedDietId, dietsData, menuData, copyFromMenuData]);

  const weekOptions = React.useMemo(() => {
    const n = Number(periodWeeks || 0);
    if (n > 0) {
      const opts = Array.from({ length: n }, (_, i) => ({ value: String(i + 1), label: `Week ${i + 1}` }));
      if (n > 1) opts.push({ value: 'all', label: 'All Weeks' });
      return opts;
    }
    // Unknown or unlimited -> only All Weeks
    return [{ value: 'all', label: 'All Weeks' }];
  }, [periodWeeks]);

  const handleAdd = () => {
    setRows((prev) => [
      ...prev,
      { week: '', meal: '', mon: '', tue: '', wed: '', thu: '', fri: '', sat: '', sun: '' },
    ]);
  };

  const handleRemove = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const createMenuMutation = useMutation({
    mutationFn: (payload: { name: string }) => dietService.createMenu({ name: payload.name, status: 'ACTIVE' }),
    onSuccess: (res) => {
      const created = (res as any)?.menu;
      if (created?.id) {
        navigate(`/setup/menus/${created.id}/manage`);
      } else {
        navigate('/setup/menus');
      }
    },
    onError: (e: any) => setError(e?.data?.message || e?.response?.data?.message || e?.message || 'Failed to create menu')
  });

  const handleSave = async () => {
    setError(null);

    if (id === 'new') {
      if (!menuName.trim()) { setError('Menu Name is required.'); return; }
      createMenuMutation.mutate({ name: menuName.trim() });
      return;
    }

    try {
      // Update menu with selected diet if changed
      if (selectedDietId && (!menuData?.menu?.dietId || menuData.menu.dietId !== selectedDietId)) {
        await dietService.updateMenu(id!, { dietId: selectedDietId });
      }

      // Keep only rows that have at least one day selected
      const hasAnyDay = (r: any) => [r.mon, r.tue, r.wed, r.thu, r.fri, r.sat, r.sun].some((v) => v && String(v).trim().length);
      const effectiveRows = rows.filter(hasAnyDay);

      // Validate week/meal only for rows that have any day selected
      const missingWeek = effectiveRows.some((r) => !r.week);
      const missingMeal = effectiveRows.some((r) => !r.meal);
      if (effectiveRows.length > 0) {
        if (missingWeek) { setError('The Week field is required for rows you filled.'); return; }
        if (missingMeal) { setError('The Meal field is required for rows you filled.'); return; }
      }

      // Build current groups and compute which previously-loaded groups must be cleared
      const groups = new Map<string, { week: string; meal: string; rows: any[] }>();
      for (const r of effectiveRows) {
        const key = `${r.week}__${r.meal}`;
        if (!groups.has(key)) groups.set(key, { week: r.week, meal: r.meal, rows: [] });
        const { mon, tue, wed, thu, fri, sat, sun } = r;
        groups.get(key)!.rows.push({ mon, tue, wed, thu, fri, sat, sun });
      }

      const prevSet = new Set((loadedGroups || []).map(g => `${g.week}__${g.meal}`));
      const newSet = new Set(Array.from(groups.keys()));
      const toClear: Array<{ week: string; meal: string }> = [];
      prevSet.forEach((key) => {
        if (!newSet.has(key)) {
          const [w, m] = key.split('__');
          toClear.push({ week: w, meal: m });
        }
      });

      if (effectiveRows.length === 0 && toClear.length === 0) {
        setError('Nothing to save. Please select at least one plate in any day, or remove existing groups.');
        return;
      }

      // Persist each group
      const groupList = Array.from(groups.values());
      console.log('Saving groups:', groupList.map(g => ({ week: g.week, meal: g.meal, rows: g.rows.length })), 'Clearing:', toClear);
      for (let i = 0; i < groupList.length; i++) {
        const g = groupList[i];
        const result = await dietService.saveMenuDayPlates(id!, { week: g.week, meal: g.meal, rows: g.rows });
        if (!result?.success) {
          throw new Error('Failed to save schedule');
        }
      }
      // Clear removed groups by sending empty rows
      for (const c of toClear) {
        const result = await dietService.saveMenuDayPlates(id!, { week: c.week, meal: c.meal, rows: [] });
        if (!result?.success) {
          throw new Error('Failed to clear schedule');
        }
      }

      // Remember last selection for this menu (last saved group if any)
      try {
        const last = groupList[groupList.length - 1];
        if (last?.week && last?.meal) {
          localStorage.setItem(`menuManageLast_${id}`, JSON.stringify({ week: String(last.week), meal: String(last.meal) }));
        }
      } catch {}

      alert('Menu schedule saved successfully!');
      // Stay on the page and refresh rows for groups we just saved (cleared groups will disappear)
      try {
        const allReloadedRows: any[] = [];
        for (const g of groupList) {
          const res = await dietService.getMenuDayPlates(id!, { week: g.week, meal: g.meal });
          const items = (res as any)?.items || [];
          if (items.length > 0) {
            const bySort: Record<number, any> = {};
            const toKey = (d: number) => (d===1?'mon':d===2?'tue':d===3?'wed':d===4?'thu':d===5?'fri':d===6?'sat':'sun');
            for (const it of items) {
              const s = Number((it as any).sortOrder || 0);
              if (!bySort[s]) bySort[s] = { week: String(g.week), meal: String(g.meal), mon:'',tue:'',wed:'',thu:'',fri:'',sat:'',sun:'' };
              const k = toKey(Number((it as any).day));
              bySort[s][k] = (it as any).recipePlateId;
            }
            const groupRows = Object.keys(bySort).map(n => Number(n)).sort((a,b)=>a-b).map(s => bySort[s]);
            allReloadedRows.push(...groupRows);
          }
        }
        if (allReloadedRows.length > 0) {
          setRows(allReloadedRows as any[]);
          setLoadedGroups(groupList.map(g => ({ week: String(g.week), meal: String(g.meal) })));
        } else {
          setRows([]);
          setLoadedGroups([]);
        }
      } catch {}

    } catch (e: any) {
      console.error('Save error:', e);
      const errorMsg = e?.data?.message || e?.response?.data?.message || e?.message || 'Failed to save schedule';
      setError(errorMsg);
    }
  };

  const handleCancel = () => navigate('/setup/menus');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }


  const handleLoadSelected = async () => {
    setError(null);
    if (!id || id === 'new') { setError('Create and save the menu first, then load.'); return; }
    const first = rows[0] || {};
    const week = first.week; const meal = first.meal;
    if (!week || !meal) { setError('Select Week and Meal in the first row, then click Load.'); return; }
    try {
      const res = await dietService.getMenuDayPlates(id!, { week, meal });
      const items = (res as any)?.items || [];
      const bySort: Record<number, any> = {};
      const toKey = (d: number) => (d===1?'mon':d===2?'tue':d===3?'wed':d===4?'thu':d===5?'fri':d===6?'sat':'sun');
      for (const it of items) {
        const s = Number((it as any).sortOrder || 0);
        if (!bySort[s]) bySort[s] = { week: String(week), meal: String(meal), mon:'',tue:'',wed:'',thu:'',fri:'',sat:'',sun:'' };
        const k = toKey(Number((it as any).day));
        bySort[s][k] = (it as any).recipePlateId;
      }
      const newRows = Object.keys(bySort).map(n => Number(n)).sort((a,b)=>a-b).map(s => bySort[s]);
      if (newRows.length === 0) { setError('No assignments found for the selected Week + Meal.'); }
      else {
        setRows(newRows);
        try { localStorage.setItem(`menuManageLast_${id}`, JSON.stringify({ week: String(week), meal: String(meal) })); } catch {}
      }
    } catch (e: any) {
      setError(e?.data?.message || e?.response?.data?.message || e?.message || 'Failed to load schedule');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Manage Menu</h1>
        <Button variant="outline" onClick={() => navigate('/setup/menus')}>Back to Menus</Button>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="flex-1">
            <label className="form-label text-lg font-semibold">Menu Name</label>
            <input
              type="text"
              className="form-input h-14 px-4 py-3 text-lg"
              value={menuName}
              onChange={(e) => setMenuName(e.target.value)}
            />
          </div>
          <div className="w-64">
            <label className="form-label text-lg font-semibold">Diet *</label>
            <select
              className="form-input h-14 px-4 py-3 text-lg"
              value={selectedDietId}
              onChange={(e) => setSelectedDietId(e.target.value)}
            >
              <option value="">Select Diet</option>
              {(dietsData?.diets || []).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className="w-64">

            <label className="form-label text-lg font-semibold">Copy From Menu</label>
            <select
              className="form-input h-14 px-4 py-3 text-lg"
              value={copyFromMenuId}
              onChange={(e) => setCopyFromMenuId(e.target.value)}
            >
              <option value="">Select</option>
              {(menusData?.menus || []).filter((m) => m.id !== id).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>
        {error && (
          <div className="mb-3 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
            <div className="text-sm text-yellow-800">⚠️ {error}</div>
          </div>
        )}


        <div className="flex items-center space-x-3 mb-4">
          <Button onClick={handleAdd}>+ Add</Button>
          <Button variant="secondary" onClick={handleLoadSelected}>Load From Selected</Button>
          <Button variant="secondary" onClick={handleSave}>Save Changes</Button>
          <Button variant="outline" onClick={handleCancel}>Cancel Changes</Button>
          <Button variant="outline" onClick={() => navigate('/setup/menus')}>Back to Menus</Button>
        </div>

        <div className="text-sm text-gray-500 mb-2">
          Drag a column header and drop it here to group by that column
        </div>

        <div className="overflow-x-auto -mx-6 px-6">
          <table className="table-auto min-w-[1400px] md:min-w-[1600px] divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Week</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Meal</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Monday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Tuesday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Wednesday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Thursday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Friday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Saturday</th>
                <th className="px-4 py-3 text-left text-base font-bold text-gray-900 uppercase whitespace-nowrap">Sunday</th>
                <th className="px-4 py-3 text-right text-base font-bold text-gray-900 uppercase whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[140px]" value={row.week} onChange={(e) => {
                      const newRows = [...rows];
                      newRows[idx].week = e.target.value;
                      setRows(newRows);
                    }}>
                      <option value="">Select</option>
                      {weekOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[160px]" value={row.meal} onChange={(e) => {
                      const newRows = [...rows];
                      newRows[idx].meal = e.target.value;
                      setRows(newRows);
                    }}>
                      <option value="">Select</option>
                      {MEAL_OPTIONS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.mon} onChange={(e) => { const nr=[...rows]; nr[idx].mon = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.tue} onChange={(e) => { const nr=[...rows]; nr[idx].tue = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.wed} onChange={(e) => { const nr=[...rows]; nr[idx].wed = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.thu} onChange={(e) => { const nr=[...rows]; nr[idx].thu = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.fri} onChange={(e) => { const nr=[...rows]; nr[idx].fri = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.sat} onChange={(e) => { const nr=[...rows]; nr[idx].sat = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
                  <td className="px-4 py-3 whitespace-nowrap"><select className="form-input h-12 px-3 py-2 text-base font-medium min-w-[220px]" value={row.sun} onChange={(e) => { const nr=[...rows]; nr[idx].sun = e.target.value; setRows(nr); }}><option value="">Select</option>{plateOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}</select></td>
						<td className="px-4 py-3 text-right whitespace-nowrap"><button type="button" className="text-red-600 hover:underline text-sm font-medium" onClick={() => handleRemove(idx)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No rows added yet. Click "+ Add" to create a new row.
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuManagementPage;

